/**
 * layerAnimation.ts
 * إعدادات عرض وحركة كل طبقة — الحقيقة الوحيدة لمنطق الحركة
 */

import type { LayerKey } from '../store/weatherStore';
import type { ForecastGridType } from './weatherLayers';

// ─── Types ───────────────────────────────────────────────────────────────────

export type LayerDisplayKind = 'heatmap' | 'raster-loop' | 'markers' | 'tracks';

/**
 * نوع الحركة فوق الطبقة:
 * - 'none'           → لا حركة
 * - 'wind-vector'    → جسيمات تتبع U/V مباشرة (طبقة الرياح)
 * - 'precip-fall'    → جسيمات تسقط رأسياً (المطر)
 * - 'wind-advection' → جسيمات تتبع حقل الرياح فوق طبقة scalar (اختياري)
 * - 'raster-loop'    → حلقة صور زمنية (رادار/قمر صناعي)
 * - 'pulse'          → نبضات (أعاصير/حرائق)
 */
export type LayerMotionKind =
    | 'none'
    | 'wind-vector'
    | 'precip-fall'
    | 'wind-advection'
    | 'raster-loop'
    | 'pulse';

export type AnimationQuality = 'low' | 'medium' | 'high';

/** إعدادات حركة الجسيمات القابلة للتخصيص من المستخدم */
export interface LayerAnimationSettings {
    particlesEnabled: boolean;
    density: number;         // 0-1  — عدد الخطوط (قليل↔كثير)
    speed: number;           // 0-1  — تسارع الخطوط (منخفض↔هام)
    trail: number;           // 0-1  — طول الخطوط (قصير↔طويل)
    opacity: number;         // 0-1  — الشفافية (شفاف جداً↔غير شفاف)
    lineWidth: number;       // 0-1  — متوسط سمك الخط (نحيف↔واسع)
    lineWidthVariation: number; // 0-1 — نثر سمك الخط (نفس السمك↔سمك متنوع)
    lifetime: number;        // 0-1  — صلاحية الخطوط (عابر↔مستمر)
    color: string;           // لون الخطوط (افتراضي أبيض)
    quality: AnimationQuality;
    reduceMotion: boolean;
}

/** وصف عرض وحركة الطبقة (ثابت لكل طبقة) */
export interface LayerPresentationConfig {
    display: LayerDisplayKind;
    motion: LayerMotionKind;
    /** هل تحتاج الحركة إلى wind grid خارجي (advection)؟ */
    requiresWindGrid: boolean;
    /** هل الجسيمات مفعّلة افتراضياً؟ */
    defaultParticles: boolean;
    /** مضاعف سرعة الحركة الخاص بالطبقة (للـadvection layers) */
    advectionSpeed: number;
    /** وصف عربي للحركة يُعرض في واجهة المستخدم */
    motionDescription: string;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

/**
 * طبقة scalar بدون حركة افتراضية، لكن مع خيار advection اختياري
 */
// مطابقة Zoom Earth: كل الطبقات العددية (حرارة/أمطار/ضغط/…) تعرض جسيمات الرياح البيضاء
// فوق الحقل اللوني افتراضياً. تبقى قابلة للإطفاء يدوياً من الإعدادات لكل طبقة.
function scalarLayer(advectionSpeed = 0.38, desc: string, defaultParticles = true): LayerPresentationConfig {
    return {
        display: 'heatmap',
        motion: 'wind-advection',
        requiresWindGrid: true,
        defaultParticles,
        advectionSpeed,
        motionDescription: desc,
    };
}

// ─── Layer presentation config ───────────────────────────────────────────────

export const LAYER_PRESENTATION: Record<LayerKey, LayerPresentationConfig> = {
    // ── Vector layers ──────────────────────────────────────────────────────
    wind: {
        display: 'heatmap',
        motion: 'wind-vector',
        requiresWindGrid: false,   // هي نفسها تملك U/V
        defaultParticles: true,
        advectionSpeed: 1.0,
        motionDescription: 'جسيمات تتبع اتجاه وسرعة الرياح الفعلية — اللون يعكس السرعة.',
    },

    // ── Scalar with optional advection ─────────────────────────────────────
    'wind-gusts': scalarLayer(
        1.0,
        'ألوان الهبّات تمثل الشدة — جسيمات الرياح البيضاء تتبع حقل الرياح الفعلي (Zoom Earth style).'
    ),
    // ── Zoom Earth style: كل الطبقات Scalar تعرض جسيمات الرياح البيضاء فوق الخريطة ──
    // لا سقوط رأسي للمطر — بل جسيمات الرياح تنساب فوق خلايا الهطول
    precipitation: scalarLayer(
        1.0,
        'خلايا الهطول ملونة — جسيمات الرياح البيضاء تنساب فوقها بنفس سرعة الرياح الفعلية.'
    ),
    'radar-sim': scalarLayer(1.0, 'رادار متوقَّع — صدى المطر والعواصف كما يحاكيه النموذج لكل ساعة قادمة.'),
    temperature: scalarLayer(1.0, 'خريطة الحرارة — جسيمات الرياح البيضاء تظهر فوقها توضيحاً لحركة الرياح.'),
    'feels-like': scalarLayer(1.0, 'الإحساس الحراري — جسيمات الرياح البيضاء تظهر فوق الخريطة.'),
    'wet-bulb': scalarLayer(1.0, 'درجة اللمبة الرطبة — جسيمات الرياح البيضاء تظهر فوق الخريطة.'),
    'k-index': scalarLayer(1.0, 'مؤشر K للعواصف الرعدية — الألوان الأعلى تعني قابلية رعدية أكبر.'),
    'o-index': scalarLayer(1.0, 'طاقة عدم الاستقرار الحملي (CAPE) — القيم الأعلى تعني عواصف رعدية أقوى محتملة، كطبقة Thunder في Windy.'),
    pressure: scalarLayer(
        1.0,
        'الضغط الجوي — جسيمات الرياح تتبع خطوط الضغط المتساوية بشكل طبيعي.'
    ),
    humidity: scalarLayer(1.0, 'الرطوبة — جسيمات الرياح البيضاء فوق الخريطة.'),
    dewpoint: scalarLayer(1.0, 'نقطة الندى — جسيمات الرياح البيضاء فوق الخريطة.'),
    clouds: scalarLayer(
        1.0,
        'الغيوم — جسيمات الرياح البيضاء تنساب فوق خريطة الغيوم.'
    ),

    // جسيمات **مطفأة افتراضياً** (وحدها بين حقول النموذج): الحقل عند 850 هكتوباسكال
    // أي نحو 1.5 كم، بينما جسيماتنا رياح 10 أمتار. المستويان يختلفان اتجاهاً وسرعةً
    // اختلافاً معتاداً (احتكاك السطح وحده يديرها عشرات الدرجات)، فتركها مشتعلة كان
    // سيرسم لساناً بارداً يزحف في اتجاه ليس اتجاهه. تبقى متاحة لمن يشغّلها عامداً —
    // فالرياح السطحية معلومة صحيحة بذاتها، وإنما لا تُقرأ كحركةٍ لهذا الحقل.
    't850-anomaly': scalarLayer(
        1.0,
        'شذوذ حرارة 850 هكتوباسكال — حقل كتلة هوائية ساكن؛ جسيمات الرياح المتاحة فوقه رياح سطحية (10 م) لا رياح هذا المستوى.',
        false
    ),

    // حقل بحري يومي: **بلا جسيمات**. جسيمات الرياح فوقه توحي بحركة سطح البحر وليست
    // كذلك — هي رياح 10 أمتار من دورة GFS الحالية فوق حقل شذوذ متوسّطه يوم كامل،
    // أي ظاهرتان بمقياسين زمنيّين لا علاقة بينهما. والصمت هنا أصدق من حركة مخترَعة.
    'sst-anomaly-rel': {
        display: 'heatmap',
        motion: 'none',
        requiresWindGrid: false,
        defaultParticles: false,
        advectionSpeed: 0,
        motionDescription: 'حقل شذوذ يومي ساكن — التغيّر يُرى عبر الشريط الزمني لا بالجسيمات.',
    },
    'sst-anomaly': {
        display: 'heatmap',
        motion: 'none',
        requiresWindGrid: false,
        defaultParticles: false,
        advectionSpeed: 0,
        motionDescription: 'حقل شذوذ يومي ساكن — التغيّر يُرى عبر الشريط الزمني لا بالجسيمات.',
    },

    // ── Tile / raster layers ───────────────────────────────────────────────
    radar: {
        display: 'raster-loop',
        motion: 'raster-loop',
        requiresWindGrid: false,
        defaultParticles: false,
        advectionSpeed: 0,
        motionDescription: 'رادار زمني بإطارات صور متتالية — لا جسيمات.',
    },
    satellite: {
        display: 'raster-loop',
        motion: 'raster-loop',
        requiresWindGrid: false,
        defaultParticles: false,
        advectionSpeed: 0,
        motionDescription: 'صور قمر صناعي — لا جسيمات، عرض مستمر.',
    },

    // ── Point overlays ─────────────────────────────────────────────────────
    hurricanes: {
        display: 'tracks',
        motion: 'pulse',
        requiresWindGrid: false,
        defaultParticles: false,
        advectionSpeed: 0,
        motionDescription: 'مسارات الأعاصير مع نبضات مرئية — لا جسيمات عامة.',
    },
    wildfires: {
        display: 'markers',
        motion: 'pulse',
        requiresWindGrid: false,
        defaultParticles: false,
        advectionSpeed: 0,
        motionDescription: 'نقاط الحرائق مع توهج نابض — لا جسيمات عامة.',
    },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * هل تدعم هذه الطبقة الجسيمات بأي شكل؟
 */
export function supportsParticles(layer: LayerKey): boolean {
    const motion = LAYER_PRESENTATION[layer].motion;
    return motion === 'wind-vector' || motion === 'precip-fall' || motion === 'wind-advection';
}

/**
 * هل يجب إظهار الجسيمات الآن (مع مراعاة الإعدادات والبيانات المتاحة)?
 */
export function shouldRenderParticles(
    layer: LayerKey,
    settings: LayerAnimationSettings,
    hasWindGrid: boolean
): boolean {
    if (!settings.particlesEnabled) return false;
    if (settings.reduceMotion) return false;

    const config = LAYER_PRESENTATION[layer];
    if (!supportsParticles(layer)) return false;

    // طبقات advection تحتاج wind grid — إذا غاب لا حركة
    if (config.requiresWindGrid && !hasWindGrid) return false;

    return true;
}

/**
 * حساب keepAlpha لتأثير الذيل بناءً على إعداد trail
 * trail=0 → ذيل قصير (keepAlpha منخفض) ← تلاشي سريع
 * trail=1 → ذيل طويل (keepAlpha عالٍ) ← يبقى طويلاً
 */
export function computeTrailAlpha(trail: number): number {
    // نسبة إبقاء الذيل لكل إطار (عند 60fps) — كلّما ارتفعت طال الخيط. يجب أن تبقى < 1
    // دائماً: أي قيمة ≥ 1 تعني ذيلاً لا يتلاشى أبداً بل يتراكم إلى تغطية كاملة للشاشة.
    // الافتراضي trail=0.55 → 0.9405: الخيط يبقى مرئياً ~49 إطاراً من الحركة. مع سرعة
    // 0.15 بكسل لكل (م/ث) لكل إطار يعطي ذلك ~33 بكسل لرياح 16 كم/س، و~74 لرياح 36 كم/س،
    // و~163 لرياح 80 كم/س، و~306 داخل إعصار — أي **نفس** الأطوال السابقة بحركة أسرع
    // مرّتين.
    //
    // لماذا عدد إطارات أقلّ؟ لأن الجسيمة تبني ذيلها بالحركة: قبل أن تعيش T إطاراً يكون
    // خيطها أقصر من طوله الصحيح. مع T=100 و عمر 199 كان **نصف** الجسيمات في طور البناء،
    // فتظهر خيوط قصيرة وأخرى طويلة في المكان الواحد بلا علاقة بسرعة الرياح هناك. مع
    // T=49 وعمر 278 تصل 82% منها إلى طولها الكامل، فيصير الطول مقروءاً كقيمة رياح.
    // ZE: fillRect أسود بشفافية 0.08 كل إطار ⇒ إبقاء 0.92؛ الافتراضي 0.55 يعطيه بالضبط.
    return Math.min(0.99, 0.865 + trail * 0.1);
}

/**
 * العمر الأقصى للجسيمة بالإطارات — دورة حياة **حتمية** كما Zoom Earth: كل جسيمة تعيش
 * هذا العدد من الإطارات بالضبط ثم تموت (لا موت احتمالي عشوائي في المنتصف).
 * يجب أن يبقى **أطول بكثير** من مدّة بناء الذيل (~49 إطاراً) وإلا قضت الجسيمة معظم
 * عمرها بخيط أقصر من طوله الصحيح فتتفاوت الأطوال بلا علاقة بسرعة الرياح.
 * lifetime=0 (عابر) → 110 إطار (~1.8ث)، lifetime=0.6 (الافتراضي) → 278 إطاراً (~4.6ث،
 * منها 82% بطول كامل)، lifetime=1 (مستمر) → 390 إطاراً.
 */
export function computeMaxAge(lifetime: number): number {
    return Math.round(110 + lifetime * 280);
}

/** الضبط المحدد مسبقاً "طبيعي" — القيم البصرية المعايَرة، تُستخدم لاستعادة الإعدادات. */
// معايرة Zoom Earth: خيوط بيضاء رفيعة (~1.2 بكسل)، طويلة الذيل، سرعتها البصرية ثابتة
// في كل الزوومات (0.075 بكسل لكل م/ث لكل إطار). كثافة الشاشة تُحسب داخل ParticleGLLayer
// من مساحة النافذة بوحدات CSS، فلا تتغيّر مع الزووم.
export const NATURAL_PARTICLE_PRESET: Pick<
    LayerAnimationSettings,
    'density' | 'speed' | 'trail' | 'opacity' | 'lineWidth' | 'lineWidthVariation' | 'lifetime' | 'color'
> = {
    // density=1 ⇒ جسيمة لكل ~1430 بكسل CSS مربّع (DENSITY_PER_CSS_PX) — الكثافة المقيسة
    // على لقطات Windy/Zoom Earth. كانت 2.0 سابقاً (ضِعف العدد) فبدت الشاشة مزدحمة.
    density: 1.0,
    // 1.0 = سرعة Zoom Earth الحرفية بلا مضاعفة.
    speed: 1.0,
    // 0.55 → إبقاء 0.9405 لكل إطار (computeTrailAlpha) = طول ذيل Windy/Zoom Earth.
    trail: 0.55,
    // ZE: لون الخيط #c3ddfa وشفافية الطبقة 2/3 في الوضع الداكن (0.8 في الفاتح).
    opacity: 0.67,
    // 0.5 → 1.5 بكسل CSS (0.85 + 0.5×1.30) = سماكة خيط Windy المقيسة.
    lineWidth: 0.5,
    lineWidthVariation: 0.35,
    // 0.6 → 199 إطاراً ≈ عمر ZE الحرفي (200).
    lifetime: 0.6,
    color: '#c3ddfa',
};

/**
 * الضبط "الطبيعي" الفعلي وقت التشغيل: قيم داشبورد الأدمن إن وصلت (مصدر الحقيقة الذي
 * يطلبه المستخدم)، وإلا ثوابت الكود أعلاه. يستخدمه زر الاستعادة في لوحة الإعدادات —
 * كان يعيد الثوابت المضمّنة دائماً فيتجاهل قيم الباك اند التي عدّلها الأدمن.
 */
export function getNaturalParticlePreset(): typeof NATURAL_PARTICLE_PRESET {
    return NATURAL_PARTICLE_PRESET;
}

/**
 * الإعدادات الافتراضية لكل طبقة
 */
export function createDefaultLayerAnimationSettings(): Record<LayerKey, LayerAnimationSettings> {
    // القيم الافتراضية المعتمَدة من لوحة إعدادات المستخدم (2026-07-13) — تبقى ثابتة بعد كل
    // تحديث للصفحة. قابلة للتخصيص من داشبورد الأدمن (/admin/particles)؛ عند توفّر تجاوز
    // بعد hydrateRemoteConfig() تُستخدم قيمه بدل الثوابت أدناه (كل الطبقات تشترك بها).
    const preset = {
        // الافتراضيات = NATURAL_PARTICLE_PRESET (مطابقة Zoom Earth الحرفية — راجع تعليقه).
        ...NATURAL_PARTICLE_PRESET,
    };

    return Object.fromEntries(
        Object.entries(LAYER_PRESENTATION).map(([layer, config]) => [
            layer,
            {
                particlesEnabled: config.defaultParticles,
                // هذه القيم تصل فعلياً إلى ParticleGLLayer (عبر ParticleWebGLLayer) — هي
                // مصدر الحقيقة الوحيد للمظهر المعتمد بصرياً (لا ثوابت خفية في الغلاف).
                // معايَر على قياس فعلي لصور Zoom Earth (كثافة معتدلة ~6-9 خيط/10k بكسل،
                // ليست ضجيجاً كثيفاً) — راجع تعليق zoomCount في ParticleGLLayer.renderInner.
                ...preset,
                quality: 'high' as AnimationQuality,
                reduceMotion: false,
            },
        ])
    ) as Record<LayerKey, LayerAnimationSettings>;
}

/**
 * Type guard — هل الطبقة من نوع forecast grid؟
 */
export function isForecastLayer(layer: LayerKey): layer is ForecastGridType {
    return !['radar', 'satellite', 'hurricanes', 'wildfires'].includes(layer);
}
